import moment from "moment";
import { ProjectPlanResponse } from "./project-plan-builder";

/**
 * Связи между работами и разделами планового графика (п. 6 ТЗ «Графики»). Хранит сервер,
 * сохраняются сразу, без черновика. Здесь — положение концов связи по плану с черновиком
 * и проверка, соблюдена ли связь: нарушенная — конфликт (п. 7 ТЗ).
 */

const API_DAY = "YYYY-MM-DD";

/** ФС, СС, ФФ, СФ — первая буква про предшественника, вторая про последователя */
export type PlanLinkType = "fs" | "ss" | "ff" | "sf";

export type PlanLinkEndKind = "work" | "section";

export const LINK_TYPE_LABELS: { [type in PlanLinkType]: string } = {
    fs: "ФС",
    ss: "СС",
    ff: "ФФ",
    sf: "СФ",
};

export const LINK_TYPE_NOTES: { [type in PlanLinkType]: string } = {
    fs: "Финиш → старт: последователь начинается после окончания предшественника",
    ss: "Старт → старт: последователь начинается не раньше начала предшественника",
    ff: "Финиш → финиш: последователь заканчивается не раньше окончания предшественника",
    sf: "Старт → финиш: последователь заканчивается не раньше начала предшественника",
};

export interface PlanLinkEndResponse {
    kind: PlanLinkEndKind;
    orderId: string;
    assignmentId: string | null;
    contentGuid: string | null;
    employerId: string | null;
    /** «0468 · КР · Волохова Р.А.» */
    label: string;
    /** Положение по опубликованному плану — на случай, если объект не попал в выборку */
    start: string | null;
    end: string | null;
}

export interface PlanLinkResponse {
    id: string;
    type: PlanLinkType;
    lag: number;
    from: PlanLinkEndResponse | null;
    to: PlanLinkEndResponse | null;
    canEdit: boolean;
}

/** Конец связи для запроса */
export interface PlanLinkEndInput {
    kind: PlanLinkEndKind;
    orderId?: string | null;
    assignmentId?: string | null;
    contentGuid?: string | null;
}

/** Результат поиска второго конца связи */
export interface PlanLinkTargets {
    orders: { id: string; label: string }[];
    targets: PlanLinkEndResponse[];
}

/** Правило без названия типа: «Последователь начинается после окончания предшественника» */
export const LINK_TYPE_RULES: { [type in PlanLinkType]: string } = {
    fs: "Последователь начинается после окончания предшественника",
    ss: "Последователь начинается не раньше начала предшественника",
    ff: "Последователь заканчивается не раньше окончания предшественника",
    sf: "Последователь заканчивается не раньше начала предшественника",
};

/** Полное название типа */
export const LINK_TYPE_TITLES: { [type in PlanLinkType]: string } = {
    fs: "Финиш → старт",
    ss: "Старт → старт",
    ff: "Финиш → финиш",
    sf: "Старт → финиш",
};

/** Когда какой тип выбирать — пример из проектирования */
export const LINK_TYPE_USAGE: { [type in PlanLinkType]: string } = {
    fs: "Строго по очереди: КР начинают, когда АР выпущен. Самый частый тип",
    ss: "Параллельно с отставанием: ОВ стартует через неделю после начала АР, когда появились подосновы",
    ff: "Закончить вместе: смета не может быть готова раньше, чем закончены чертежи раздела",
    sf: "Редкий: последователь не может закончиться, пока предшественник не начался (передача смены, дежурство)",
};

/** Склонение «рабочий день» для числа */
export function workDaysWord(count: number): string {
    const n = Math.abs(count) % 100;
    const last = n % 10;

    if (n > 10 && n < 20) {
        return "раб. дней";
    }

    return last === 1 ? "раб. день" : last >= 2 && last <= 4 ? "раб. дня" : "раб. дней";
}

/** Что значит лаг: «0 — без паузы», «+2 — пауза 2 раб. дня», «−1 — нахлёст на 1 раб. день» */
export function lagNote(lag: number): string {
    if (lag > 0) {
        return `+${lag} — пауза ${lag} ${workDaysWord(lag)} между ними`;
    }

    if (lag < 0) {
        return `−${-lag} — нахлёст: можно раньше на ${-lag} ${workDaysWord(lag)}`;
    }

    return "0 — без паузы и без нахлёста";
}

/**
 * Связь словами на конкретных объектах: «„КР · Иванов“ начнётся не раньше чем через 2 раб. дня
 * после окончания „АР · Петров“». Правила — те же, что в checkLink
 */
export function linkSentence(type: PlanLinkType, lag: number, from: string, to: string): string {
    const a = `«${from}»`;
    const b = `«${to}»`;
    const days = (count: number) => `${count} ${workDaysWord(count)}`;

    // что ограничено у последователя и от какого края предшественника отсчёт
    const verb = type === "fs" || type === "ss" ? "начнётся" : "закончится";
    const edge = type === "fs" || type === "ff" ? "окончания" : "начала";

    if (type === "fs") {
        if (lag > 0) {
            return `${b} начнётся не раньше чем через ${days(lag)} после окончания ${a}`;
        }

        if (lag < 0) {
            return `${b} может начаться за ${days(-lag)} до окончания ${a} (нахлёст), но не раньше`;
        }

        return `${b} начнётся не раньше следующего рабочего дня после окончания ${a}`;
    }

    if (lag > 0) {
        return `${b} ${verb} не раньше чем через ${days(lag)} после ${edge} ${a}`;
    }

    if (lag < 0) {
        return `${b} ${verb} не раньше чем за ${days(-lag)} до ${edge} ${a}`;
    }

    return `${b} ${verb} не раньше ${edge} ${a}`;
}

/** «ФС», «ФС +2», «СС −1» */
export function linkCaption(type: PlanLinkType, lag: number): string {
    const label = LINK_TYPE_LABELS[type] || "ФС";
    return lag === 0 ? label : lag > 0 ? `${label} +${lag}` : `${label} −${-lag}`;
}

/** Ключ конца: у работы — работа, у раздела — проект и раздел */
export function endKey(end: PlanLinkEndInput | PlanLinkEndResponse): string {
    return end.kind === "work" ? `work:${end.assignmentId}` : `section:${end.orderId}/${end.contentGuid}`;
}

/** Где стоит объект: первый и последний день, yyyy-MM-dd */
export interface EndPlace {
    start: string;
    end: string;
    /** Объект есть в выборке графика — его видно на экране */
    visible: boolean;
    /** Куски работы, разделённые дырой, по возрастанию — только если их больше одного */
    parts?: { start: string; end: string }[];
}

/** Какой конец связи: предшественник (from) или последователь (to) */
export type LinkRole = "from" | "to";

/**
 * Положение концов по ответу графика с черновиком: работа — по её отрезкам, раздел —
 * по заданному сроку, а без него по отрезкам его строки. Чего в выборке нет — по датам,
 * которые сервер положил в саму связь (опубликованный план).
 */
export function endPlaces(response: ProjectPlanResponse | null): Map<string, EndPlace> {
    const places = new Map<string, EndPlace>();

    const widen = (key: string, from: string, to: string) => {
        const place = places.get(key);

        if (!place) {
            places.set(key, { start: from, end: to, visible: true });
        } else {
            place.start = from < place.start ? from : place.start;
            place.end = to > place.end ? to : place.end;
        }
    };

    // куски работ: отрезки одной работы, между которыми есть пустые рабочие дни
    const pieces = new Map<string, { start: string; end: string }[]>();

    (response ? response.orders || [] : []).forEach((order) => {
        (order.rows || []).forEach((row) =>
            (row.segments || []).forEach((segment) => {
                if (segment.assignmentId && !segment.ghost) {
                    const key = `work:${segment.assignmentId}`;
                    // настоящий конец отрезка может лежать за краем выборки — stopDay
                    const end = segment.stopDay || segment.to;

                    widen(key, segment.from, end);
                    pieces.set(key, [...(pieces.get(key) || []), { start: segment.from, end }]);
                }
            })
        );

        const terms = new Map((order.terms || []).map((term) => [term.contentGuid || "", term]));

        (order.sections || []).forEach((section) => {
            if (!section.id) {
                return;
            }

            const key = `section:${order.id}/${section.id}`;
            const term = terms.get(section.id);

            if (term && term.start && term.end) {
                places.set(key, { start: term.start, end: term.end, visible: true });
                return;
            }

            (section.rows || []).forEach((row) =>
                (row.segments || []).forEach((segment) => {
                    if (!segment.ghost) {
                        widen(key, segment.from, segment.stopDay || segment.to);
                    }
                })
            );
        });
    });

    if (pieces.size) {
        const calendar = new WorkDays(
            (response ? response.periods || [] : []).flatMap((period) =>
                (period.days || []).filter((day) => !day.isDayOff).map((day) => day.day)
            )
        );

        pieces.forEach((list, key) => {
            const parts: { start: string; end: string }[] = [];

            list.sort((left, right) => (left.start < right.start ? -1 : 1)).forEach((piece) => {
                const last = parts[parts.length - 1];

                // соседние отрезки без пустого рабочего дня между ними — один кусок
                if (last && piece.start <= calendar.shift(last.end, 1)) {
                    last.end = piece.end > last.end ? piece.end : last.end;
                } else {
                    parts.push({ ...piece });
                }
            });

            const place = places.get(key);

            if (place && parts.length > 1) {
                place.parts = parts;
            }
        });
    }

    return places;
}

/**
 * Где стоит конец связи. У работы, разорванной дырой (сдвиг «с даты» оставил прошлую часть
 * на месте), связь относится не ко всей работе, а к куску — так же, как при применении
 * черновика её делит сервер: исходящая — от последнего куска, входящая — к первому, если он
 * не целиком в прошлом (прошлое — уже история). Без роли — вся работа целиком.
 */
export function placeOf(
    end: PlanLinkEndResponse,
    places: Map<string, EndPlace>,
    role?: LinkRole,
    today: string = moment().format(API_DAY)
): EndPlace | null {
    const place = places.get(endKey(end));

    if (place) {
        if (!role || !place.parts) {
            return place;
        }

        const alive = place.parts.filter((part) => part.end >= today);
        const parts = alive.length ? alive : place.parts;
        const part = role === "from" ? parts[parts.length - 1] : parts[0];

        return { start: part.start, end: part.end, visible: place.visible };
    }

    return end.start && end.end ? { start: end.start, end: end.end, visible: false } : null;
}

/** Рабочие дни: колонки сетки, за её краями — будни (праздничного календаря там нет) */
export class WorkDays {
    private readonly index: Map<string, number>;

    constructor(private readonly days: string[]) {
        this.index = new Map(days.map((day, position) => [day, position]));
    }

    /** День через shift рабочих дней; выходной сначала приводится к рабочему в сторону сдвига */
    shift(day: string, shift: number): string {
        const position = this.index.get(day);

        if (position !== undefined && position + shift >= 0 && position + shift < this.days.length) {
            return this.days[position + shift];
        }

        const step = shift >= 0 ? 1 : -1;
        let date = moment(day, API_DAY);
        let steps = Math.abs(shift);

        while (steps > 0) {
            date = date.add(step, "day");

            if (this.isWorkDay(date.format(API_DAY))) {
                steps--;
            }
        }

        return date.format(API_DAY);
    }

    private isWorkDay(day: string): boolean {
        if (this.days.length && day >= this.days[0] && day <= this.days[this.days.length - 1]) {
            return this.index.has(day);
        }

        const weekday = moment(day, API_DAY).isoWeekday();
        return weekday < 6;
    }
}

export interface LinkCheck {
    /** Какой край последователя ограничен: его начало (ФС, СС) или конец (ФФ, СФ) */
    edge: "start" | "end";
    /** Не раньше какого дня должен быть этот край */
    required: string;
    /** Где он сейчас */
    actual: string;
    violated: boolean;
}

/**
 * Соблюдена ли связь. ФС: последователь начинается не раньше чем через 1 + лаг рабочих
 * дней после конца предшественника; СС — не раньше начала + лаг; ФФ — кончается не
 * раньше конца + лаг; СФ — кончается не раньше начала + лаг.
 */
export function checkLink(
    type: PlanLinkType,
    lag: number,
    from: EndPlace,
    to: EndPlace,
    calendar: WorkDays
): LinkCheck {
    const fromEdge = type === "fs" || type === "ff" ? from.end : from.start;
    const offset = type === "fs" ? 1 + lag : lag;
    const required = calendar.shift(fromEdge, offset);
    const edge = type === "fs" || type === "ss" ? "start" : "end";
    const actual = edge === "start" ? to.start : to.end;

    return { edge, required, actual, violated: actual < required };
}
